/**
 * Phone Lookup Client — single source of truth for the Twilio line lookup.
 *
 * Calls the `phone-lookup` Supabase Edge Function (a Twilio Lookup v2 proxy)
 * for a pass/fail on whether a number is a real, reachable LINE. NOT OTP.
 *
 * Both validators (phone-step-validator for the Next gate + message, and
 * validation-enhancements for the green status icon) read from here, so a
 * given number is looked up ONCE and both agree.
 *
 * Fail-open: any network/server error settles as 'error', which callers treat
 * as a pass — a Twilio outage must never block a lead. Only a definitive
 * 'invalid' blocks.
 */

import config from '../../config/unified';

export type LookupState = 'valid' | 'invalid' | 'pending' | 'error' | 'none';

const settled = new Map<string, 'valid' | 'invalid' | 'error'>();
const inflight = new Set<string>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

/** Normalize an (already format-validated) US phone to E.164: +1XXXXXXXXXX. */
export function toE164US(raw: string): string | null {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if ((raw || '').trim().startsWith('+') && digits.length >= 8) return `+${digits}`;
  return null;
}

/** Subscribe to "a lookup just settled" — re-run field validation in the cb. */
export function onLookupSettled(cb: () => void): void {
  listeners.add(cb);
}

/** Current lookup state for a number (synchronous; no network). */
export function getLookupState(raw: string): LookupState {
  const e164 = toE164US(raw);
  if (!e164) return 'invalid';
  const s = settled.get(e164);
  if (s) return s;
  if (inflight.has(e164)) return 'pending';
  return 'none';
}

/**
 * Ensure a lookup runs for this number — debounced (350ms) and deduped per
 * number, so rapid keystrokes and two callers collapse to one network call.
 * No-op if the number is already settled or in flight.
 */
export function ensureLookup(raw: string): void {
  const e164 = toE164US(raw);
  if (!e164) return;
  if (settled.has(e164) || inflight.has(e164)) return;

  const prev = timers.get(e164);
  if (prev) clearTimeout(prev);
  timers.set(
    e164,
    setTimeout(() => {
      timers.delete(e164);
      if (settled.has(e164) || inflight.has(e164)) return;
      inflight.add(e164);
      doLookup(e164).then((state) => {
        inflight.delete(e164);
        settled.set(e164, state);
        listeners.forEach((l) => {
          try {
            l();
          } catch (e) {
            console.warn('[phone-lookup] listener error', e);
          }
        });
      });
    }, 350),
  );
}

/** Best-effort current session id (so lookup logs tie to a lead). */
function getSessionId(): string | null {
  try {
    const raw =
      sessionStorage.getItem('webflow_session') || localStorage.getItem('webflow_session');
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s?.id || s?.sessionId || s?.session_id || null;
  } catch {
    return null;
  }
}

async function doLookup(e164: string): Promise<'valid' | 'invalid' | 'error'> {
  try {
    const res = await fetch(config.edgeFunctions.endpoints.phoneLookup, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: config.supabase.anonKey,
        Authorization: `Bearer ${config.supabase.anonKey}`,
      },
      credentials: 'omit', // Supabase uses wildcard CORS — required (see CLAUDE.md)
      body: JSON.stringify({ phone: e164, session_id: getSessionId() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success !== true) {
      console.warn('[phone-lookup] non-OK response — failing open', res.status);
      return 'error';
    }
    return data.valid === true ? 'valid' : 'invalid';
  } catch (e) {
    console.warn('[phone-lookup] request failed — failing open', e);
    return 'error';
  }
}
